import PropTypes from "prop-types";
import * as React from "react";
import { pricingFor } from "../../../domain/productComposer/pricing";
import SignType from "../../../domain/signType";
import { usd } from "../../../util/formatCurrency";
import { NumberInput, Switch, Typography } from "../../../themeV2";
import SignTypeChip from "../../SignTypeChip/SignTypeChip";
import css from "./SignTypeCard.module.css";

const toDollars = (cents) => (cents ?? 0) / 100;
const toCents = (dollars) => Math.round((dollars ?? 0) * 100);

const idFor = (signedType) =>
  `sign-type-${signedType.toLowerCase().replace(/[^a-z]+/g, "-")}`;

const StockField = function StockField({ stock, readOnly, onChange }) {
  const isLimited = stock !== null && stock !== undefined;

  return (
    <div className={css.stock}>
      <div className={css.inputHeight}>
        <Switch
          label="Limit quantity"
          checked={isLimited}
          disabled={readOnly}
          onCheckedChange={(limited) => onChange(limited ? 1 : null)}
        />
      </div>
      {isLimited && (
        <NumberInput
          className={css.stockInput}
          label="Quantity"
          min={0}
          max={9999}
          disabled={readOnly}
          value={stock}
          onValueChange={(next) => onChange(next ?? 0)}
        />
      )}
    </div>
  );
};

const SplitInput = function SplitInput({
  signer,
  signedType,
  isOnlySigner,
  readOnly,
  dispatch,
}) {
  return (
    <NumberInput
      className={css.moneyInput}
      steppers={false}
      label={isOnlySigner ? "You earn" : `${signer.name} earns`}
      min={0}
      max={10000}
      step={1}
      disabled={readOnly}
      format={{ style: "currency", currency: "USD" }}
      value={toDollars(signer.splits[signedType])}
      onValueChange={(dollars) =>
        dispatch({
          type: "setSplit",
          influencerId: signer.influencerId,
          signedType,
          amount: toCents(dollars),
        })
      }
    />
  );
};

SplitInput.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isOnlySigner: PropTypes.bool,
  readOnly: PropTypes.bool,
  signedType: PropTypes.string.isRequired,
  signer: PropTypes.object.isRequired,
};

StockField.propTypes = {
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  stock: PropTypes.number,
};

const SignTypeCard = function SignTypeCard({
  signedType,
  isOn,
  canToggle,
  signers,
  visibleSignerIds,
  stock,
  bumpFacts,
  readOnly,
  dispatch,
}) {
  const id = idFor(signedType);
  const label = SignType.toLabel(signedType);
  const hint = SignType.toHint(signedType);
  const shown = signers.filter((s) =>
    visibleSignerIds.includes(s.influencerId),
  );
  const { total } = pricingFor({
    splits: signers.map((s) => s.splits[signedType] ?? 0),
    signedType,
    ...bumpFacts,
  });
  const unpaid = shown.filter((s) => (s.splits[signedType] ?? 0) === 0);

  return (
    <li className={css.card} data-on={isOn || undefined}>
      <div className={css.header}>
        <div className={css.identity}>
          <span id={`${id}-label`}>
            <SignTypeChip signedType={signedType} label={label} />
          </span>
          {hint && (
            <Typography
              variant="body-sm"
              color="variant"
              as="p"
              id={`${id}-hint`}
            >
              {hint}
            </Typography>
          )}
        </div>
        {canToggle && (
          <Switch
            checked={isOn}
            disabled={readOnly}
            aria-labelledby={`${id}-label`}
            aria-describedby={hint ? `${id}-hint` : undefined}
            onCheckedChange={() =>
              dispatch({ type: "toggleSignedType", signedType })
            }
          />
        )}
      </div>

      {isOn && (
        <div className={css.body}>
          {shown.map((signer, index) => (
            <div key={signer.influencerId} className={css.row}>
              <SplitInput
                signer={signer}
                signedType={signedType}
                isOnlySigner={shown.length === 1}
                readOnly={readOnly}
                dispatch={dispatch}
              />
              {index === shown.length - 1 && (
                <StockField
                  stock={stock}
                  readOnly={readOnly}
                  onChange={(next) =>
                    dispatch({ type: "setStock", signedType, stock: next })
                  }
                />
              )}
            </div>
          ))}

          <Typography
            variant="body-md"
            color="variant"
            as="p"
            className={css.total}
            aria-live="polite"
          >
            Customer pays {usd(total)}
          </Typography>

          {unpaid.length > 0 && (
            <Typography variant="body-sm" color="variant" as="p" role="status">
              {unpaid.map((s) => s.name).join(", ")}{" "}
              {unpaid.length === 1 ? "earns" : "earn"} $0 here. You&apos;ll be
              asked to confirm before saving.
            </Typography>
          )}
        </div>
      )}
    </li>
  );
};

SignTypeCard.propTypes = {
  bumpFacts: PropTypes.object.isRequired,
  canToggle: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  isOn: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  signedType: PropTypes.string.isRequired,
  signers: PropTypes.arrayOf(PropTypes.object).isRequired,
  stock: PropTypes.number,
  visibleSignerIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default SignTypeCard;
